import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, resource } from '@angular/core';
import { lastValueFrom, map } from 'rxjs';
import { Region } from '../../models/region.model';
import { RegionService } from '../region/region.service';
import { mapPermissionResponseToModel } from './permissions.mapper';

export interface PermissionResponse {
  permission_id: string;
  permission_name: string;
  regions: { region_id: number; region_name: string }[];
}

export interface Permission {
  id: string;
  name: string;
  regions: Region[];
  group: string;
}

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  http = inject(HttpClient);
  regions = inject(RegionService);

  permissions$ = this.http
    .get<PermissionResponse[]>('auth/get-functional-permissions')
    .pipe(
      map((permissions) =>
        permissions.map((permission) =>
          mapPermissionResponseToModel(permission, this.regions.regions()),
        ),
      ),
      map((permissions) =>
        this.groupArrayOfObjects<Permission>(permissions, 'group'),
      ),
    );

  permissions = resource({
    loader: () => lastValueFrom(this.permissions$),
  });

  permissionsForGroup = (group: string) => {
    const permissions = this.permissions.value();
    return permissions ? permissions[group] : [];
  };

  permissionGroups = computed(() => {
    const p = this.permissions.value();
    return p ? Object.keys(p) : [];
  });

  groupArrayOfObjects<T>(array: T[], key: keyof T): Record<string, T[]> {
    return array.reduce(
      (accumulator, currentValue) => {
        const groupKey = String(currentValue[key]); // Ensure key is a string for object keys
        if (!accumulator[groupKey]) {
          accumulator[groupKey] = [];
        }
        accumulator[groupKey].push(currentValue);
        return accumulator;
      },
      {} as Record<string, T[]>,
    );
  }
}
